/**
 * <roast-machine-root> custom element.
 *
 * Attaches an open shadow root, injects the brand stylesheet, and mounts
 * the AppShell. Style isolation is provided by the shadow DOM boundary,
 * which is the primary defence against host-page CSS (e.g. Squarespace
 * themes) leaking in.
 *
 * The element reads its API base URL from `data-api-base` (default `/api`)
 * and forwards it to the app shell, which owns the api client wiring.
 */

import brandCss from '../styles/brand.css?raw';
import { mountAppShell, type AppShellTeardown } from './AppShell';

const ELEMENT_NAME = 'roast-machine-root';
const STYLE_MARKER_ATTR = 'data-roast-brand-style';
const DEFAULT_API_BASE = '/api';

export class RoastMachineRoot extends HTMLElement {
  private teardown: AppShellTeardown | null = null;

  connectedCallback(): void {
    // Defensive: if a previous disconnect left a teardown in flight (e.g.
    // the element was detached and re-attached without its shadow DOM
    // being torn down), run it before mounting again.
    if (this.teardown !== null) {
      this.teardown();
      this.teardown = null;
    }

    const shadow = this.shadowRoot ?? this.attachShadow({ mode: 'open' });

    // Reuse the existing <style> node if the shadow root persisted across
    // a disconnect/connect cycle; otherwise inject a fresh one.
    const existingStyle = shadow.querySelector(
      `style[${STYLE_MARKER_ATTR}]`,
    );
    if (existingStyle === null) {
      const style = document.createElement('style');
      style.setAttribute(STYLE_MARKER_ATTR, '');
      style.textContent = brandCss;
      shadow.appendChild(style);
    }

    const apiBase = this.getAttribute('data-api-base') ?? DEFAULT_API_BASE;
    this.teardown = mountAppShell(shadow, { apiBase });
  }

  disconnectedCallback(): void {
    if (this.teardown !== null) {
      this.teardown();
      this.teardown = null;
    }
  }
}

if (customElements.get(ELEMENT_NAME) === undefined) {
  customElements.define(ELEMENT_NAME, RoastMachineRoot);
}
